import { useState } from 'react';
import { Home, RotateCcw } from 'lucide-react';
import BaseModal from '../../../../components/BaseComponents/BaseModal/BaseModal';
import HeaderActions from '../../../../components/FooterSettings/HeaderActionBar';
import { useToast } from '../../../../context/ToastContext';
import { useHomepageConfig } from '../../../../hooks/useHomepageConfig';
import { useUploadHomepageImage } from '../../../../api/layout/useHomepageConfigApi';
import HomepageSettingsSections from './components/HomepageSettingsSections';
import { useHomepageSettingsForm } from './hooks/useHomepageSettingsForm';
import styles from './HomepageSettings.module.css';
import '../../../../styles/cms-form-controls.css';

export default function HomepageSettings() {
  const { showToast } = useToast();
  const { config, loading, saving, error, isDirty, updateField, save, reset } = useHomepageConfig();
  const { uploadImage, loading: uploadLoading, error: uploadError } = useUploadHomepageImage();
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const formHelpers = useHomepageSettingsForm({
    config,
    updateField,
    uploadImage,
    showToast,
  });

  const handleSave = async () => {
    if (uploadLoading) {
      showToast('Please wait for current image upload to finish', 'warning');
      return;
    }
    try {
      await save();
      showToast('Homepage settings saved successfully', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save homepage settings', 'error');
    }
  };

  const handleCancel = () => {
    reset();
  };

  if (loading) {
    return (
      <div className={`${styles.page} cms-form-scope`}>
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>⌂</div>
            <div>
              <h1>CMS - Homepage Configuration</h1>
              <p>Loading…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.page} cms-form-scope`}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <Home size={18} />
          </div>
          <div>
            <h1>CMS - Homepage Configuration</h1>
            <p>Manage homepage content and visual sections from this dashboard.</p>
            <span className={styles.coolTag}>Smart Content Editor</span>
          </div>
        </div>
        <button type="button" className={styles.resetBtn} onClick={() => setShowResetConfirm(true)}>
          <RotateCcw size={14} />
          Reset All
        </button>
      </div>

      <BaseModal
        open={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        title="Reset homepage settings"
        width="440px"
        footer={
          <div className={styles.modalActions}>
            <button type="button" className={styles.modalBtn} onClick={() => setShowResetConfirm(false)}>
              Cancel
            </button>
            <button
              type="button"
              className={`${styles.modalBtn} ${styles.modalPrimary}`}
              onClick={() => {
                handleCancel();
                setShowResetConfirm(false);
              }}
            >
              Reset
            </button>
          </div>
        }
      >
        <p className={styles.modalText}>
          Reset all homepage settings to last saved values? Unsaved changes will be discarded.
        </p>
      </BaseModal>

      {error && <div className={styles.errorBox}>{error}</div>}
      {uploadError && <div className={styles.errorBox}>{uploadError}</div>}
      <HomepageSettingsSections
        config={config}
        updateField={updateField}
        uploadLoading={uploadLoading}
        uploadError={uploadError}
        helpers={formHelpers}
      />

      <HeaderActions
        saved={!isDirty && !saving}
        saving={saving}
        onCancel={handleCancel}
        onSave={handleSave}
        saveLabel="Save Homepage Configuration"
      />
    </div>
  );
}
